import { InputAdornment, OutlinedInput } from "@mui/material";

import { Box } from "ui/box/box";
import { IconButton } from "ui/icon-button/icon-button";
import { InputControl } from "ui/forms/input-control/input-control";
import { mergeSx } from "ui/merge-sx";
import { type TextFieldProps } from "ui/text-field/text-field.types";
import { ACTION_ROW_SX, CONTROL_SX, getControlSx } from "ui/text-field/text-field.styles";
import { useTextField } from "ui/text-field/use-text-field";

import { INPUT_ICON_CLASS } from "theme/components/field-geometry";

import Icons from "assets";

const CLEAR_LABEL = "Clear";

export const TextField = ({
  ref,
  id,
  name,
  value,
  label,
  required,
  disabled,
  readOnly,
  errMessage,
  helperText,
  infoTooltip,
  actionTrigger,
  inputActions,
  startIcon,
  endIcon,
  startAdornment,
  endAdornment,
  onClear,
  size = "medium",
  placeholder,
  "data-testid": dataTestId,
  slotProps,
  sx,
  controlSx,
  inputSx,
  actionRowSx,
  ...rest
}: TextFieldProps) => {
  const { fieldId, inputRef, showClear, hasInputActions, hasEndContent, handleClear, describedBy, testId } =
    useTextField({
      id,
      name,
      ref,
      value,
      disabled,
      readOnly,
      errMessage,
      helperText,
      endIcon,
      inputActions,
      onClear,
      "data-testid": dataTestId,
    });

  return (
    <InputControl
      id={fieldId}
      label={label}
      infoTooltip={infoTooltip}
      required={required}
      disabled={disabled}
      errorMessage={errMessage}
      helperText={helperText}
      size={size}
      sx={sx}>
      <Box sx={mergeSx(ACTION_ROW_SX, actionRowSx)}>
        <Box sx={CONTROL_SX}>
          <OutlinedInput
            // Ahead of `rest` so a call site that computes its own description still out-ranks it.
            aria-describedby={describedBy}
            {...rest}
            value={value}
            inputRef={inputRef}
            id={fieldId}
            name={name}
            placeholder={placeholder ?? ""}
            disabled={disabled}
            readOnly={readOnly}
            error={!!errMessage}
            fullWidth
            size={size}
            sx={getControlSx(hasInputActions, inputSx, controlSx)}
            startAdornment={
              startIcon ? (
                <InputAdornment position="start">
                  <Box component="span" className={INPUT_ICON_CLASS}>
                    {startIcon}
                  </Box>
                </InputAdornment>
              ) : (
                startAdornment
              )
            }
            endAdornment={
              hasEndContent ? (
                <InputAdornment position="end">
                  {endIcon && (
                    <Box component="span" className={INPUT_ICON_CLASS}>
                      {endIcon}
                    </Box>
                  )}
                  {showClear && (
                    <IconButton aria-label={CLEAR_LABEL} onClick={handleClear} variant="text">
                      <Icons.QuickTipClose />
                    </IconButton>
                  )}
                  {inputActions}
                </InputAdornment>
              ) : (
                endAdornment
              )
            }
            slotProps={{
              ...slotProps,
              input: { "data-testid": testId, ...slotProps?.input },
            }}
          />
        </Box>
        {actionTrigger}
      </Box>
    </InputControl>
  );
};
